/**
 * 模組頁面代碼 : 1430-UI-02
 * 頁面說明 : 新增修改員工畫面
 * @module web/modules/employee/components/EmployeeModal
 */

// ----- import from npm modules ----
import $ from 'jquery';
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

// ----- import from local modules -----
import enumList from 'web/utils/enumList';
import {BaseComponent} from 'web/components/BaseComponent';

import {Typeahead, Menu, MenuItem} from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';

// ----- constants -----
import {TITLE_EMPLOYEE_NEW, TITLE_EMPLOYEE_EDIT} from 'web/constant';
import {showMessage} from 'web/utils/modalHelper';
import {addEmployee, editEmployee} from 'web/redux/actions/EmployeeActions';
import {fetchDepartment} from 'web/redux/actions/DepartmentActions';
import {fetchPosition} from 'web/redux/actions/PositionActions';
import {fetchConditionSelection} from 'web/redux/actions/ConditionActions';
import {EmployeeAreaEnumMappings} from '../hooks/useEmployee';

const EmployeeModal = React.forwardRef((props, ref) => {
  const {
    toggleEmployeeAddModal,
    toggleEmployeeEditModal,
    reloadEmployeeList,
    mode,
    employeeItem,
    departmentSelection,
    positionSelection,
    conditionSelection,
  } = props;
  const dispatch = useDispatch();
  const baseComponent = new BaseComponent(props);
  const [modalEmployeeCode, setModalEmployeeCode] = useState('');
  const [modalEmployeeName, setModalEmployeeName] = useState('');
  const [modalDepartment, setModalDepartment] = useState([]);
  const [modalPosition, setModalPosition] = useState([]);
  const [modalEmail, setModalEmail] = useState('');
  const [modalConditions, setModalConditions] = useState([]);
  const [modalAreaID, setModalAreaID] = useState([]);
  const checkedAllRef = useRef(false);
  const [invalidShow, setInvalidShow] = useState('none');
  // let modalIcon = mode === enumList.modalMode.new ? '#ico-plus' : '#ico-edit';
  const modalTitleText =
    mode === enumList.modalMode.new ? TITLE_EMPLOYEE_NEW : TITLE_EMPLOYEE_EDIT;

  const handleSubmit = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    // 檢查表單是否驗證通過
    if (form.checkValidity() === false) {
      setInvalidShow('block');
      return;
    }
    form.classList.add('was-validated');

    const req = {
      employeeCode: modalEmployeeCode,
      employeeName: modalEmployeeName,
      departmentCode: modalDepartment[0].departmentCode,
      positionCode: modalPosition[0].positionCode,
      conditions: modalConditions,
      email: modalEmail,
      areaID: modalAreaID?.[0]?.value,
    };

    if (mode === enumList.modalMode.edit) {
      submitEditEmployee(req);
      toggleEmployeeEditModal(false);
    }
    if (mode === enumList.modalMode.new) {
      submitAddEmployee(req);
      toggleEmployeeAddModal(false);
    }

    form.classList.remove('was-validated');
    resetForm();
  };

  const resetForm = () => {
    setModalEmployeeCode('');
    setModalEmployeeName('');
    setModalDepartment([]);
    setModalPosition([]);
    setModalEmail('');
    setModalAreaID([]);
    setModalConditions([]);
    checkedAllRef.current = false;
  };

  const handleChange = (event) => {
    const {value} = event.target;

    if (modalConditions.includes(value)) {
      setModalConditions(modalConditions.filter((item) => item !== value));
    } else {
      setModalConditions([...modalConditions, value]);
    }
  };

  const handleCheckAll = () => {
    if (checkedAllRef.current) {
      setModalConditions([]);
    } else {
      setModalConditions(
        conditionSelection.map(({conditionCode}) => conditionCode),
      );
    }
    checkedAllRef.current = !checkedAllRef.current;
  };

  const submitAddEmployee = (req) => {
    dispatch(addEmployee(req))
      .then((res) => {
        const statusCode = res.payload.code;
        if (statusCode.isSuccessCode()) {
          const departmentList = res.payload.result;
          showMessage(enumList.messageModalMode.info, '新增成功');
          toggleEmployeeAddModal(false);
          reloadEmployeeList();
        } else {
          toggleEmployeeAddModal(false);
          showMessage(
            enumList.messageModalMode.error,
            statusCode,
            res.payload.message,
          );

          console.error('fetchDepartmentList error', res);
        }
      })
      .catch((err) => {
        showMessage(enumList.messageModalMode.error, 'error', err);
        console.error('fetchDepartmentList error', err);
      });
  };
  const submitEditEmployee = (req) => {
    dispatch(editEmployee(req)).then((res) => {
      const statusCode = res.payload.code;
      if (statusCode.isSuccessCode()) {
        const departmentList = res.payload.result;
        showMessage(enumList.messageModalMode.info, '修改成功');
        toggleEmployeeEditModal(false);
        reloadEmployeeList();
        console.log('fetchDepartmentList', departmentList);
      } else {
        toggleEmployeeEditModal(false);
        showMessage(
          enumList.messageModalMode.error,
          'error',
          res.payload.message,
        );
      }
    });
  };

  useEffect(() => {
    setModalEmployeeCode(employeeItem ? employeeItem.employeeCode : '');
    setModalEmployeeName(employeeItem ? employeeItem.employeeName : '');
    const conditionCodes = employeeItem
      ? employeeItem.conditions.map((item) => item.conditionCode)
      : [];
    setModalConditions(employeeItem ? conditionCodes : []);
    setModalEmail(employeeItem ? employeeItem.email : '');
    setModalAreaID(
      employeeItem?.areaID
        ? [EmployeeAreaEnumMappings.get(employeeItem.areaID)]
        : [],
    );
    if (departmentSelection && positionSelection) {
      const departmentData = departmentSelection.filter(
        (item) => item.departmentName === employeeItem?.departmentName,
      );
      setModalDepartment(departmentData);
      const positionData = positionSelection.filter(
        (item) => item.positionName === employeeItem?.positionName,
      );
      setModalPosition(positionData);
    }
  }, [employeeItem, conditionSelection]);

  return (
    <div
      ref={ref}
      className="modal fade"
      id="modalDepartment"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabIndex="-1"
      aria-labelledby="modalDepartmentLabel"
    >
      <div
        className="modal-dialog modal-dialog-centered
        modal-dialog-scrollable"
      >
        <form
          className="modal__form modal-content needs-validation"
          id="modalAccountForm"
          noValidate
          onSubmit={handleSubmit}
        >
          <div className="modal-header">
            <h1 className="modal-title fs-5 px-2" id="modalAccountLabel">
              <div className="modal__title__text">{modalTitleText}</div>
            </h1>
            <button
              type="button"
              className="btn-close btn-cancel"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={resetForm}
            ></button>
          </div>
          <div className="modal-body">
            <div className="d-flex flex-column gap-3 px-2">
              <div className="modal__form__catagory row">
                <div className="col-12">
                  <div className="row">
                    <div className="col-6">
                      <label htmlFor="modalEmployeeName" className="form-label">
                        員工姓名*
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        id="modalEmployeeName"
                        name="modalEmployeeName"
                        value={modalEmployeeName}
                        onChange={(e) => setModalEmployeeName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label htmlFor="modalEmployeeCode" className="form-label">
                        員工編號*
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        id="modalEmployeeCode"
                        name="modalEmployeeCode"
                        value={modalEmployeeCode}
                        onChange={(e) => setModalEmployeeCode(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label htmlFor="departmentName" className="form-label">
                        部門*
                      </label>
                      <Typeahead
                        id="departmentName"
                        size="sm"
                        onChange={setModalDepartment}
                        selected={modalDepartment}
                        inputProps={{required: true}}
                        value={modalDepartment}
                        options={departmentSelection}
                        labelKey="departmentName"
                        placeholder="請輸入部門名稱"
                        renderInput={({
                          inputRef,
                          referenceElementRef,
                          ...inputProps
                        }) => (
                          <input
                            {...inputProps}
                            className="rbt-input-main form-control
                            rbt-input form-control-sm form-select"
                            ref={(node) => {
                              inputRef(node);
                              referenceElementRef(node);
                            }}
                          />
                        )}
                        renderMenu={(results, menuProps) => (
                          <Menu
                            id={menuProps.id}
                            innerRef={menuProps.innerRef}
                            maxHeight="70px"
                            style={menuProps.style}
                          >
                            {results.map((result, index) => (
                              <MenuItem
                                option={result}
                                position={index}
                                key={index}
                              >
                                {result.departmentName}
                              </MenuItem>
                            ))}
                          </Menu>
                        )}
                      />
                    </div>
                    <div className="col-6">
                      <label htmlFor="positionName" className="form-label">
                        職位*
                      </label>
                      <Typeahead
                        id="positionName"
                        size="sm"
                        onChange={setModalPosition}
                        selected={modalPosition}
                        inputProps={{required: true}}
                        value={modalPosition}
                        options={positionSelection}
                        labelKey="positionName"
                        placeholder="請輸入職位名稱"
                        renderInput={({
                          inputRef,
                          referenceElementRef,
                          ...inputProps
                        }) => (
                          <input
                            {...inputProps}
                            className="rbt-input-main form-control
                            rbt-input form-control-sm form-select"
                            ref={(node) => {
                              inputRef(node);
                              referenceElementRef(node);
                            }}
                          />
                        )}
                        renderMenu={(results, menuProps) => (
                          <Menu
                            id={menuProps.id}
                            innerRef={menuProps.innerRef}
                            maxHeight="70px"
                            style={menuProps.style}
                          >
                            {results.map((result, index) => (
                              <MenuItem
                                option={result}
                                position={index}
                                key={index}
                              >
                                {result.positionName}
                              </MenuItem>
                            ))}
                          </Menu>
                        )}
                      />
                    </div>
                    <div className="col-6">
                      <label
                        htmlFor="employee-area-edit"
                        className="form-label"
                      >
                        負責地區
                      </label>
                      <Typeahead
                        id="employee-area-edit"
                        selected={modalAreaID}
                        onChange={(areaID) => setModalAreaID(areaID)}
                        options={[...EmployeeAreaEnumMappings.values()]}
                        size="sm"
                        placeholder="請選擇負責區域"
                      />
                    </div>
                    <div className="col-6">
                      <label htmlFor="modalUserPosition" className="form-label">
                        email
                      </label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        id="modalDepartmentName"
                        name="modalDepartmentName"
                        value={modalEmail}
                        onChange={(e) => setModalEmail(e.target.value)}
                        // required
                      />
                    </div>
                    <div className="col-12">
                      <div className="d-flex justify-content-between m-2">
                        <label
                          htmlFor="modalEmployeeCondition"
                          className="form-label"
                        >
                          員工技能
                        </label>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={handleCheckAll}
                          type="button"
                        >
                          全選
                        </button>
                      </div>
                      <div className="row g-2">
                        {conditionSelection.length > 0 &&
                          conditionSelection.map((condition, index) => (
                            <div className="col-4" key={index}>
                              <input
                                type="checkbox"
                                className="btn-check"
                                name="modalEmployeeCondition"
                                id={`modalEmployeeCondition${condition.conditionCode}`}
                                autoComplete="off"
                                value={condition.conditionCode}
                                onChange={handleChange}
                                checked={modalConditions.includes(
                                  condition.conditionCode,
                                )}
                              />
                              <label
                                className="btn btn-sm btn-outline-secondary"
                                htmlFor={`modalEmployeeCondition${condition.conditionCode}`}
                              >
                                {condition.conditionName}
                              </label>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <div
                    className="alert alert-warning global-invalid-feedback"
                    style={{display: invalidShow}}
                  ></div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-cancel"
              data-bs-dismiss="modal"
              onClick={resetForm}
            >
              取消
            </button>
            <button type="submit" className="btn btn-primary">
              儲存
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

export default EmployeeModal;
